// The demo venue's simulation (spec/24 §2.2): pure functions over one serialisable state.
//
// **Time drives it.** A session stores when it started and the time bought for it; whether it is running, ending
// soon, in grace or over is computed from the clock each time it is asked (`sessionPhase`), so the console tab
// and the kiosk tab agree at every instant without either one ticking anything. A session that runs out ends by
// itself: nothing is written when it does.
//
// **The venue has a life of its own.** A short script, timed from when the demo opened (`epoch`), places a food
// order, starts a walk-in and raises a call for help (`advance`). Each step runs once — its id is kept — and at
// the instant it was due, not when a tab noticed, so two tabs applying it agree.
//
// **The visitor changes it.** Every action is a function from a state to a new state, or a refusal in the words
// the kiosk and the console already have for it. Nothing here reads a clock (`now` is always a parameter) or
// touches storage — `store.ts` does that.
import { DEMO_GRACE_SECONDS, DEMO_MENU_PRODUCTS, DEMO_PLAYERS, DEMO_STATIONS, DEMO_WARNING_MINUTES, classById, passById, playerById, productById, stationById, } from "./venue.js";
const SECOND = 1_000;
const MINUTE = 60 * SECOND;
export const DEMO_ORDER_STATUSES = ['new', 'accepted', 'preparing', 'ready', 'delivered', 'cancelled'];
export const DEMO_CALL_CATEGORIES = ['pc', 'peripherals', 'food', 'other'];
// -----------------------------------------------------------------------------------------------
// Ids
// -----------------------------------------------------------------------------------------------
const CROCKFORD = '0123456789abcdefghjkmnpqrstvwxyz';
function base32(value, length) {
    let rest = Math.max(0, Math.floor(value));
    let out = '';
    for (let index = 0; index < length; index += 1) {
        out = CROCKFORD[rest % 32] + out;
        rest = Math.floor(rest / 32);
    }
    return out;
}
/**
 * A TypeID for something the simulation made: unique within one venue's life, and different after a Reset (the
 * epoch is in it), so a screen's cache never mistakes a new session for an old one with the same number.
 */
export function mintId(prefix, epoch, counter) {
    return `${prefix}_0${base32(epoch, 11)}d3m0${base32(counter, 10)}`;
}
function nextId(state, prefix) {
    const counter = state.counter + 1;
    return { id: mintId(prefix, state.epoch, counter), state: { ...state, counter } };
}
export function sessionPhase(session, now) {
    const totalSeconds = session.fundedSeconds + session.added.reduce((sum, entry) => sum + entry.seconds, 0);
    const endsAt = session.startedAt + totalSeconds * SECOND;
    const graceEndsAt = endsAt + DEMO_GRACE_SECONDS * SECOND;
    const base = { totalSeconds, endsAt, graceEndsAt };
    if (session.endedAt !== null && session.endedAt <= now) {
        return { ...base, status: 'ended', secondsLeft: 0, endedAt: session.endedAt, endReason: session.endReason };
    }
    if (now >= graceEndsAt)
        return { ...base, status: 'ended', secondsLeft: 0, endedAt: graceEndsAt, endReason: 'time_up' };
    if (now >= endsAt)
        return { ...base, status: 'grace', secondsLeft: 0, endedAt: null, endReason: null };
    const secondsLeft = Math.max(0, Math.ceil((endsAt - now) / SECOND));
    return { ...base, status: session.lockedAt === null ? 'running' : 'locked', secondsLeft, endedAt: null, endReason: null };
}
/** The low-time warnings (minutes left) a session has passed by `now`, in the order it passed them. */
export function warningsPassed(session, now) {
    const phase = sessionPhase(session, now);
    if (phase.status === 'ended')
        return [];
    return [...DEMO_WARNING_MINUTES]
        .sort((a, b) => b - a)
        .filter((minutes) => phase.endsAt - minutes * MINUTE <= now && phase.totalSeconds > minutes * 60);
}
/** The session on a station that has not ended by `now`, if any. */
export function currentSession(state, stationId, now) {
    for (let index = state.sessions.length - 1; index >= 0; index -= 1) {
        const session = state.sessions[index];
        if (session !== undefined && session.stationId === stationId && sessionPhase(session, now).status !== 'ended')
            return session;
    }
    return null;
}
/** The player's session that has not ended, wherever it is. */
export function sessionOfPlayer(state, playerId, now) {
    for (let index = state.sessions.length - 1; index >= 0; index -= 1) {
        const session = state.sessions[index];
        if (session !== undefined && session.playerId === playerId && sessionPhase(session, now).status !== 'ended')
            return session;
    }
    return null;
}
export function sessionById(state, sessionId) {
    return state.sessions.find((session) => session.id === sessionId);
}
export function stationView(state, station, now) {
    const stationClass = classById(station.classId);
    if (stationClass === undefined)
        throw new Error(`demo: station ${station.id} has no class`);
    const session = currentSession(state, station.id, now);
    const phase = session === null ? null : sessionPhase(session, now);
    const player = session?.playerId == null ? null : (playerById(session.playerId) ?? null);
    const mode = phase === null ? 'locked' : phase.status === 'locked' ? 'session_locked' : 'in_session';
    return { station, stationClass, session, phase, player, mode };
}
export function stationViews(state, now) {
    return DEMO_STATIONS.map((station) => stationView(state, station, now));
}
export function walletOf(state, playerId) {
    return state.wallets[playerId] ?? 0;
}
const ok = (state, value) => ({ ok: true, state, value });
const refuse = (refusal) => ({ ok: false, refusal });
/** Most a wallet-funded session opens with: an hour, or what the wallet buys if that is less. */
const WALLET_SESSION_SECONDS = 3_600;
function replaceSession(state, next) {
    return { ...state, sessions: state.sessions.map((session) => (session.id === next.id ? next : session)) };
}
/**
 * Start a session. A player's opening time is their pass, or what their wallet buys at the station's class rate
 * (an hour at most); with neither, `no-funding`. A staff start names its own time.
 */
export function startSession(state, input) {
    const station = stationById(input.stationId);
    const stationClass = station === undefined ? undefined : classById(station.classId);
    if (station === undefined || stationClass === undefined)
        return refuse('not-found');
    if (currentSession(state, station.id, input.now) !== null)
        return refuse('station-busy');
    if (input.playerId !== null) {
        if (playerById(input.playerId) === undefined)
            return refuse('not-found');
        if (sessionOfPlayer(state, input.playerId, input.now) !== null)
            return refuse('already-in-session');
    }
    let next = state;
    let fundedSeconds;
    let funding;
    if (input.staff !== undefined) {
        fundedSeconds = input.staff.seconds;
        funding = { kind: input.staff.kind, label: input.staff.label };
    }
    else {
        if (input.playerId === null)
            return refuse('no-funding');
        const pass = state.passes[input.playerId] ?? null;
        const wallet = walletOf(state, input.playerId);
        const usePass = pass !== null && input.prefer !== 'wallet';
        if (usePass) {
            fundedSeconds = pass.seconds;
            funding = { kind: 'pass', label: pass.name };
            next = { ...next, passes: { ...next.passes, [input.playerId]: null } };
        }
        else {
            const affordable = Math.floor((wallet / stationClass.hourlyRate) * 3_600);
            fundedSeconds = Math.min(WALLET_SESSION_SECONDS, affordable);
            if (fundedSeconds < 60)
                return refuse('no-funding');
            const cost = Math.ceil((fundedSeconds / 3_600) * stationClass.hourlyRate);
            funding = { kind: 'wallet', label: 'Wallet' };
            next = { ...next, wallets: { ...next.wallets, [input.playerId]: wallet - cost } };
        }
    }
    const minted = nextId(next, 'ses');
    const session = {
        id: minted.id,
        stationId: station.id,
        playerId: input.playerId,
        startedAt: input.now,
        fundedSeconds,
        funding,
        added: [],
        lockedAt: null,
        endedAt: null,
        endReason: null,
        origin: input.origin,
    };
    return ok({ ...minted.state, sessions: [...minted.state.sessions, session] }, session);
}
/** Time given or sold to a running session (or one in grace, which it brings back). */
export function addTime(state, input) {
    const session = sessionById(state, input.sessionId);
    if (session === undefined)
        return refuse('not-found');
    if (sessionPhase(session, input.now).status === 'ended')
        return refuse('session-ended');
    const next = { ...session, added: [...session.added, { at: input.now, seconds: input.seconds, kind: input.kind, label: input.label }] };
    return ok(replaceSession(state, next), next);
}
/** The player buys a pass from their wallet at the kiosk, for the session they are in. */
export function buyPassWithWallet(state, input) {
    const session = sessionById(state, input.sessionId);
    const pass = passById(input.passId);
    if (session === undefined || pass === undefined || session.playerId === null)
        return refuse('not-found');
    const wallet = walletOf(state, session.playerId);
    if (wallet < pass.price)
        return refuse('wallet-insufficient-funds');
    const added = addTime(state, { sessionId: session.id, seconds: pass.seconds, kind: 'pass', label: pass.name, now: input.now });
    if (!added.ok)
        return added;
    return ok({ ...added.state, wallets: { ...added.state.wallets, [session.playerId]: wallet - pass.price } }, added.value);
}
/** The player keeps playing on their wallet at the kiosk: time at the PC's class rate, paid from the wallet now. */
export function addTimeFromWallet(state, input) {
    const session = sessionById(state, input.sessionId);
    if (session === undefined || session.playerId === null)
        return refuse('not-found');
    const stationClass = classById(stationById(session.stationId)?.classId ?? '');
    if (stationClass === undefined)
        return refuse('not-found');
    const cost = Math.ceil((input.seconds / 3_600) * stationClass.hourlyRate);
    const wallet = walletOf(state, session.playerId);
    if (wallet < cost)
        return refuse('wallet-insufficient-funds');
    const added = addTime(state, { sessionId: session.id, seconds: input.seconds, kind: 'wallet', label: 'Wallet', now: input.now });
    if (!added.ok)
        return added;
    return ok({ ...added.state, wallets: { ...added.state.wallets, [session.playerId]: wallet - cost } }, added.value);
}
export function endSession(state, input) {
    const session = sessionById(state, input.sessionId);
    if (session === undefined)
        return refuse('not-found');
    if (sessionPhase(session, input.now).status === 'ended')
        return refuse('session-ended');
    const next = { ...session, endedAt: input.now, endReason: input.reason, lockedAt: null };
    // An open call for help from a station whose session ended is over too.
    const calls = state.calls.map((call) => call.stationId === session.stationId && (call.state === 'open' || call.state === 'acknowledged') ? { ...call, state: 'cancelled', updatedAt: input.now } : call);
    return ok({ ...replaceSession(state, next), calls }, next);
}
export function lockSession(state, input) {
    const session = sessionById(state, input.sessionId);
    if (session === undefined)
        return refuse('not-found');
    if (sessionPhase(session, input.now).status !== 'running')
        return refuse('session-ended');
    const next = { ...session, lockedAt: input.now };
    return ok(replaceSession(state, next), next);
}
export function unlockSession(state, input) {
    const session = sessionById(state, input.sessionId);
    if (session === undefined)
        return refuse('not-found');
    if (sessionPhase(session, input.now).status !== 'locked')
        return refuse('not-locked');
    const next = { ...session, lockedAt: null };
    return ok(replaceSession(state, next), next);
}
/**
 * A food order from a PC. It is paid from the player's wallet when that covers it, and otherwise waits to be paid
 * at the desk; either way it goes to the kitchen at once.
 */
export function placeOrder(state, input) {
    if (stationById(input.stationId) === undefined)
        return refuse('not-found');
    const lines = [];
    for (const line of input.lines) {
        const product = productById(line.productId);
        if (product === undefined)
            return refuse('not-found');
        if (line.quantity < 1)
            continue;
        lines.push({ productId: product.id, name: product.name, quantity: Math.min(20, Math.floor(line.quantity)), unitPrice: product.price });
    }
    if (lines.length === 0)
        return refuse('nothing-ordered');
    const total = lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);
    let next = state;
    let paidBy = 'desk';
    if (input.playerId !== null && input.payWith !== 'desk') {
        const wallet = walletOf(state, input.playerId);
        if (wallet >= total) {
            paidBy = 'wallet';
            next = { ...next, wallets: { ...next.wallets, [input.playerId]: wallet - total } };
        }
    }
    const minted = nextId(next, 'fod');
    const order = {
        id: minted.id,
        number: ORDER_NUMBER_BASE + minted.state.orders.length,
        stationId: input.stationId,
        playerId: input.playerId,
        lines,
        placedAt: input.now,
        status: 'new',
        updatedAt: input.now,
        paidBy,
        origin: input.origin,
    };
    return ok({ ...minted.state, orders: [...minted.state.orders, order] }, order);
}
/** The first order number of the day. */
const ORDER_NUMBER_BASE = 421;
export function setOrderStatus(state, input) {
    const order = state.orders.find((candidate) => candidate.id === input.orderId);
    if (order === undefined)
        return refuse('not-found');
    const next = { ...order, status: input.status, updatedAt: input.now };
    return ok({ ...state, orders: state.orders.map((candidate) => (candidate.id === next.id ? next : candidate)) }, next);
}
export function raiseCall(state, input) {
    if (stationById(input.stationId) === undefined)
        return refuse('not-found');
    const open = state.calls.find((call) => call.stationId === input.stationId && (call.state === 'open' || call.state === 'acknowledged'));
    if (open !== undefined)
        return ok(state, open);
    const minted = nextId(state, 'cll');
    const call = {
        id: minted.id,
        stationId: input.stationId,
        playerId: input.playerId,
        category: input.category,
        note: input.note,
        openedAt: input.now,
        state: 'open',
        updatedAt: input.now,
        staffName: null,
    };
    return ok({ ...minted.state, calls: [...minted.state.calls, call] }, call);
}
export function setCallState(state, input) {
    const call = state.calls.find((candidate) => candidate.id === input.callId);
    if (call === undefined)
        return refuse('not-found');
    const next = { ...call, state: input.state, updatedAt: input.now, staffName: input.staffName === undefined ? call.staffName : input.staffName };
    return ok({ ...state, calls: state.calls.map((candidate) => (candidate.id === next.id ? next : candidate)) }, next);
}
/** The player cancels their own open call at the kiosk. No open call is not an error. */
export function cancelCallAt(state, input) {
    return {
        ...state,
        calls: state.calls.map((call) => call.stationId === input.stationId && (call.state === 'open' || call.state === 'acknowledged') ? { ...call, state: 'cancelled', updatedAt: input.now } : call),
    };
}
export function sendMessage(state, input) {
    if (stationById(input.stationId) === undefined)
        return refuse('not-found');
    const minted = nextId(state, 'cmd');
    const message = { id: minted.id, stationId: input.stationId, text: input.text.slice(0, 280), at: input.now };
    return ok({ ...minted.state, messages: [...minted.state.messages, message] }, message);
}
// -----------------------------------------------------------------------------------------------
// The opening venue, and its script
// -----------------------------------------------------------------------------------------------
const STATION = (number) => {
    const station = DEMO_STATIONS.find((candidate) => candidate.number === number);
    if (station === undefined)
        throw new Error(`demo: no PC ${number}`);
    return station;
};
const PLAYER = (username) => {
    const player = DEMO_PLAYERS.find((candidate) => candidate.username === username);
    if (player === undefined)
        throw new Error(`demo: no player ${username}`);
    return player;
};
/** A menu product's id by its name, for the opening venue and the script. */
function productIdNamed(name) {
    const product = DEMO_MENU_PRODUCTS.find((candidate) => candidate.name === name);
    if (product === undefined)
        throw new Error(`demo: no product named ${name}`);
    return product.id;
}
/**
 * The venue when the demo opens, `epoch` being that instant: five PCs in session at different points of their
 * time — one ending soon, one running out within minutes, one stepped away — three free, and a kitchen ticket
 * on the go. Every player's wallet and pass as `venue.ts` gives them, less what the opening sessions used.
 */
export function createDemoState(epoch) {
    const wallets = {};
    const passes = {};
    for (const player of DEMO_PLAYERS) {
        wallets[player.id] = player.wallet;
        passes[player.id] = player.pass;
    }
    let state = {
        version: 1,
        epoch,
        sessions: [],
        orders: [],
        calls: [],
        messages: [],
        wallets,
        passes,
        script: [],
        counter: 0,
        revision: 0,
    };
    const opening = (number, username, minutesAgo, minutes, kind, locked) => {
        const player = PLAYER(username);
        const minted = nextId(state, 'ses');
        const session = {
            id: minted.id,
            stationId: STATION(number).id,
            playerId: player.id,
            startedAt: epoch - minutesAgo * MINUTE,
            fundedSeconds: minutes * 60,
            funding: kind === 'pass' ? { kind, label: player.pass?.name ?? `${minutes / 60} hours` } : { kind, label: 'Wallet' },
            added: [],
            lockedAt: locked === undefined ? null : epoch - locked * MINUTE,
            endedAt: null,
            endReason: null,
            origin: 'venue',
        };
        state = {
            ...minted.state,
            sessions: [...minted.state.sessions, session],
            ...(kind === 'pass' ? { passes: { ...minted.state.passes, [player.id]: null } } : {}),
        };
    };
    // PC 01: ten minutes left — "ending soon" from the first second, in grace at +10 min, over at +12 min.
    opening(1, 'aarav', 50, 60, 'wallet');
    // PC 02: a two-hour pass, 25 minutes in.
    opening(2, 'rohang', 25, 120, 'pass');
    // PC 04: four minutes left — its last warnings, grace at +4 min, over at +6 min.
    opening(4, 'ishaan', 56, 60, 'wallet');
    // PC 05: stepped away three minutes ago, screen locked.
    opening(5, 'meera', 15, 60, 'wallet', 3);
    // PC 07 (Pro): a three-hour pass, forty minutes in.
    opening(7, 'ananya_i', 40, 180, 'pass');
    // The kitchen is making PC 05's roll.
    const roll = placeOrder(state, {
        stationId: STATION(5).id,
        playerId: PLAYER('meera').id,
        lines: [{ productId: productIdNamed('Paneer tikka roll'), quantity: 1 }],
        now: epoch - 6 * MINUTE,
        origin: 'venue',
    });
    if (roll.ok) {
        const preparing = setOrderStatus(roll.state, { orderId: roll.value.id, status: 'preparing', now: epoch - 4 * MINUTE });
        if (preparing.ok)
            state = preparing.state;
    }
    return state;
}
const keep = (result, otherwise) => (result.ok ? result.state : otherwise);
/**
 * The venue's own life (spec/24 §2.2): a food order at one minute, a walk-in at three (on PC 03, if the visitor
 * has not taken it), a call for help at five and another order at eight. A step that cannot happen — its PC is
 * busy, its player gone — is skipped, and still counted as run.
 */
export const DEMO_SCRIPT = [
    {
        id: 'order-pc02',
        at: 60,
        apply: (state, at) => keep(placeOrder(state, {
            stationId: STATION(2).id,
            playerId: PLAYER('rohang').id,
            lines: [
                { productId: productIdNamed('Masala fries'), quantity: 1 },
                { productId: productIdNamed('Cold coffee'), quantity: 1 },
            ],
            now: at,
            origin: 'venue',
        }), state),
    },
    {
        id: 'walk-in-pc03',
        at: 180,
        apply: (state, at) => keep(startSession(state, {
            stationId: STATION(3).id,
            playerId: PLAYER('arjun').id,
            now: at,
            origin: 'staff',
            staff: { seconds: 45 * 60, kind: 'paid', label: '45 minutes' },
        }), state),
    },
    {
        id: 'call-pc07',
        at: 300,
        apply: (state, at) => keep(raiseCall(state, { stationId: STATION(7).id, playerId: PLAYER('ananya_i').id, category: 'peripherals', note: 'My headset has no sound.', now: at }), state),
    },
    {
        id: 'order-pc07',
        at: 480,
        apply: (state, at) => keep(placeOrder(state, {
            stationId: STATION(7).id,
            playerId: PLAYER('ananya_i').id,
            lines: [
                { productId: productIdNamed('Chicken momos'), quantity: 1 },
                { productId: productIdNamed('Fresh lime soda'), quantity: 2 },
            ],
            now: at,
            origin: 'venue',
        }), state),
    },
];
/**
 * Run every script step due by `now` that has not run, each at its own instant, in order. Returns the same object
 * when nothing was due, so a caller can tell whether to save.
 */
export function advance(state, now) {
    let next = state;
    for (const step of DEMO_SCRIPT) {
        const at = state.epoch + step.at * SECOND;
        if (at > now || next.script.includes(step.id))
            continue;
        next = { ...step.apply(next, at), script: [...next.script, step.id] };
    }
    return next;
}
//# sourceMappingURL=engine.js.map